import { DynamicContainer, Form, FormSection, Icon } from '@hai/ui-react';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormSelect, SelectOptions } from 'src/js/component/base/form/form-select';
import { FormValue } from 'src/js/component/base/form/form-value';
import systemInfo, { systemInfoCapabilities, updateSystemInfo } from 'src/js/data/systemInfo';
import { useStores } from 'src/js/hook/use-stores';
import { notificationHandler } from 'src/js/notification/notification-handler';
import { DeviceStatusViewModel } from 'src/js/pages/settings/status/device-status-view-model';
import { formatUptime } from 'src/js/pages/streaming/stream/stream-helper';
import { RebootNeeded, RebootNeededRef } from 'src/js/reboot/reboot-needed';
import { useWatch } from 'src/js/store/use-watch';
import { generateDataAuto } from 'src/js/util/automation';
import { constant, PRODUCT } from 'src/js/util/constant';
import { FormChangeEvent } from 'src/js/util/global-type';
interface Props {
  model: DeviceStatusViewModel;
}

export const DeviceStatus: React.FunctionComponent<Props> = ({ model }) => {
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const personalityOptions: SelectOptions = [{ value: 'SDI', label: 'SDI' }];
  if (constant.product === PRODUCT.X4) {
    personalityOptions.push({ value: 'DEFENSE', label: 'DEFENSE' });
  }
  if (constant.product === PRODUCT.X4 && systemInfo.videoInputs > 1) {
    personalityOptions.push({ value: 'SQD', label: 'SQD' });
  }
  if (systemInfo.has2110license && !systemInfoCapabilities.isRugged()) {
    personalityOptions.push({ value: '2110', label: t('settings.personality.2110') });
  }

  const rebootNeededRef = useRef<RebootNeededRef>(null);
  const handleChangePersonality = (formEvent: FormChangeEvent) => {
    const { value } = formEvent.target;
    updateSystemInfo({ personality: value, nextPersonality: value }).then((res: any) => {
      if (res.ok) {
        if (value !== systemInfo.personality) {
          rebootNeededRef.current.show();
        }
      }
      notificationHandler(
        res,
        t('settings.system.notifications.updateSuccessMsg'),
        t('settings.system.notifications.updateErrorMsg'),
        t,
      );
    });
  };

  const [personality, setPersonality] = useState(systemInfo.nextPersonality);
  useWatch(systemInfo, 'nextPersonality', setPersonality);

  const showPersonalityDropdown = personalityOptions.length > 1;

  return (
    <>
      <Form className="hai-mb-6">
        <FormSection title={t('settings.system.section.deviceStatus.title')}>
          {showPersonalityDropdown && (
            <DynamicContainer minColumns={3}>
              {sessionStore.isAdmin() ? (
                <FormSelect
                  name="personality"
                  title={t('settings.system.section.deviceStatus.personality')}
                  defaultValue={personality}
                  options={personalityOptions}
                  onChange={handleChangePersonality}
                />
              ) : (
                <FormValue label={t('settings.system.section.deviceStatus.personality')} value={personality} />
              )}
              {systemInfo.personality !== systemInfo.nextPersonality && (
                <div className="warning-info-container">
                  <Icon iconname="StatusWarning" className="hai-mr-4" />
                  {t('settings.system.section.deviceStatus.personalityReboot')}
                </div>
              )}
            </DynamicContainer>
          )}
          <DynamicContainer>
            <FormValue
              label={t('settings.system.section.deviceStatus.cardStatus')}
              value={model?.cardStatus}
              icon={model?.cardStatusIconStatusType}
            />
            <FormValue label={t('settings.system.section.deviceStatus.cardType')} value={model?.cardType} />
            <FormValue label={t('settings.system.section.deviceStatus.partNumber')} value={model?.partNumber} />
            <FormValue
              mask
              label={t('settings.system.section.deviceStatus.serialNumber')}
              value={model?.serialNumber}
              data-auto={generateDataAuto('form-value', 'serial number')}
            />
            <FormValue
              label={t('settings.system.section.deviceStatus.firmwareVersion')}
              value={model?.firmwareVersion}
              data-auto={generateDataAuto('form-value', 'firmware version')}
            />
            <FormValue
              label={t('settings.system.section.deviceStatus.firmwareDate')}
              value={
                model?.firmwareDate
                  ? moment(model?.firmwareDate, 'MMM D YYYY').format(constant.momentFormat.status)
                  : ''
              }
            />
            <FormValue
              label={t('settings.system.section.deviceStatus.hardwareVersion')}
              value={model?.hardwareRevision}
            />
            <FormValue
              label={t('settings.system.section.deviceStatus.hardwareCompatibility')}
              value={model?.hardwareCompatibility}
            />
            <FormValue label={t('settings.system.section.deviceStatus.cpldVersion')} value={model?.cpldRevision} />
            <FormValue
              label={t('settings.system.section.deviceStatus.systemUptime')}
              value={formatUptime(model?.uptime, true)}
              data-auto={generateDataAuto('form-value', 'system uptime')}
            />
            <FormValue
              label={t('settings.system.section.deviceStatus.chipsetLoad')}
              value={model?.chipsetLoad ? `${model?.chipsetLoad}%` : ''}
            />
            <FormValue
              label={t('settings.system.section.deviceStatus.temperature')}
              value={model?.temperature ? `${model?.temperature}°C` : ''}
            />
          </DynamicContainer>
        </FormSection>
      </Form>
      <RebootNeeded ref={rebootNeededRef} />
    </>
  );
};
